import { useId, useState } from "react";
import type { ServedQuestion } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ChoiceRow } from "@/components/ui/ChoiceGroup";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";
import { LimitNote, Note, overLimit } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { TEXT_LIMITS } from "@/lib/text-limits";
import type { useQuestionWrites } from "@/hooks/use-questions";

type Writes = ReturnType<typeof useQuestionWrites>;

// v0.29's words; its last sentence holds while an open question blocks Generate (decision 53).
const LATER_WARNING =
  "The question will be added to Open Questions in the left menu. You will need to answer it there before you can generate the patent draft.";

const NOT_SENT = "Your reply was not saved.";

/** v0.29's question before a question is put off for later, in its own words. */
function SkipWarning({
  pending,
  onKeep,
  onSkip,
}: {
  pending: boolean;
  onKeep: () => void;
  onSkip: () => void;
}) {
  const id = useId();
  return (
    <Panel
      role="group"
      aria-labelledby={id}
      tone="warning"
      pad="snug"
      className="mt-3 flex flex-col gap-2"
    >
      <b id={id} className="text-[12.5px]">
        Are you sure?
      </b>
      <p className="text-xs">{LATER_WARNING}</p>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="dense" onClick={onKeep}>
          Keep answering
        </Button>
        <Button size="dense" disabled={pending} onClick={onSkip}>
          Ask me later
        </Button>
      </div>
    </Panel>
  );
}

/** The question's options: one chosen, or several, each pressed when chosen. */
function Choices({
  question,
  single,
  chosen,
  onChoose,
}: {
  question: ServedQuestion;
  single: boolean;
  chosen: readonly string[];
  onChoose: (option: string) => void;
}) {
  return (
    <div
      role={single ? "radiogroup" : "group"}
      aria-label={question.question}
      className="flex flex-col items-start gap-2"
    >
      {(question.options ?? []).map((option) => {
        const on = chosen.includes(option);
        return (
          <ChoiceRow
            key={option}
            role={single ? "radio" : "checkbox"}
            checked={on}
            onChoose={() => {
              onChoose(option);
            }}
          >
            {option}
          </ChoiceRow>
        );
      })}
    </div>
  );
}

/**
 * The reply being written: the options chosen, the words, whether it can be sent, and the
 * sending. A refused reply keeps its words, with the server's reason, for Reply to send again.
 */
function useReplyDraft(
  question: ServedQuestion,
  writes: Writes,
  onDone: () => void,
) {
  const [refusal, setRefusal] = useState<string | null>(null);
  // A refused reply lives only here, so it counts as unsaved until it lands.
  useUnsavedDraft(refusal !== null);
  const [choices, setChoices] = useState<string[]>(
    question.draft?.choices ?? [],
  );
  const [text, setText] = useState(question.draft?.text ?? "");
  const chatOnly = question.response_type === "chat_only";
  const single = question.response_type === "single_choice";
  const said = text.trim();
  const choose = (option: string) => {
    setChoices((held) =>
      single
        ? [option]
        : held.includes(option)
          ? held.filter((c) => c !== option)
          : [...held, option],
    );
  };
  const send = () => {
    setRefusal(null);
    writes.reply.mutate(
      {
        questionId: question.question_id,
        draft: { choices, text: said === "" ? null : said },
      },
      {
        onSuccess: onDone,
        onError: (failure) => {
          setRefusal(refusalDetail(failure, NOT_SENT));
        },
      },
    );
  };
  return {
    choices,
    choose,
    text,
    setText,
    said,
    send,
    refusal,
    ready:
      !overLimit(said, TEXT_LIMITS.answer) &&
      (chatOnly ? said !== "" : choices.length > 0),
    chatOnly,
    single,
  };
}

/** The question on screen: its options, a comment, and Reply or Skip. */
export function ReplyControls({
  question,
  writes,
  canSkip = true,
  onDone,
}: {
  question: ServedQuestion;
  writes: Writes;
  /** Whether Skip is offered: at a commit, never on the review. */
  canSkip?: boolean;
  /** The reply or the skip has landed. */
  onDone: () => void;
}) {
  const draft = useReplyDraft(question, writes, onDone);
  const { choices, choose, text, setText, said, ready, chatOnly } = draft;
  const [warning, setWarning] = useState(false);
  if (warning) {
    return (
      <SkipWarning
        pending={writes.skip.isPending}
        onKeep={() => {
          setWarning(false);
        }}
        onSkip={() => {
          writes.skip.mutate(question.question_id, { onSuccess: onDone });
        }}
      />
    );
  }
  return (
    <div className="mt-2.5 flex flex-col gap-2.5">
      {!chatOnly && (
        <Choices
          question={question}
          single={draft.single}
          chosen={choices}
          onChoose={choose}
        />
      )}
      <div className="flex items-end gap-2">
        <Textarea
          rows={1}
          aria-label={chatOnly ? "Your answer" : "Add a comment (optional)"}
          placeholder={chatOnly ? "Your answer" : "Add a comment (optional)"}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          className="min-w-0 flex-1"
        />
        {canSkip && !question.draft && (
          <Button
            variant="secondary"
            size="dense"
            onClick={() => {
              setWarning(true);
            }}
          >
            Ask me later
          </Button>
        )}
        <Button
          size="dense"
          disabled={!ready || writes.reply.isPending}
          onClick={draft.send}
        >
          Reply
        </Button>
      </div>
      <LimitNote length={said.length} max={TEXT_LIMITS.answer} />
      {draft.refusal !== null && (
        <Note tone="destructive" role="alert">
          {`${draft.refusal} Your reply is kept here: press Reply to send it again.`}
        </Note>
      )}
    </div>
  );
}
